import React, { useState } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  makeStyles,
  Box,
  Typography,
  InputLabel,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import { InfoOutlined as InfoIcon } from "@material-ui/icons";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import PopoverInfoSection from "./PopoverInfoSection";

const useStyles = (clientColor) =>
  makeStyles((theme) => ({
    ...customSelectStyle(theme, clientColor),
    sectionContainer: {
      border: `1px solid ${grayColor[8]}`,
      borderLeft: `4px solid ${clientColor || theme.palette.primary.main}`,
      borderRadius: theme.spacing(1),
      padding: theme.spacing(2),
      backgroundColor: grayColor[12],
      position: "relative",
    },
    sectionTitle: {
      fontSize: "0.95rem",
      fontWeight: 500,
      marginBottom: theme.spacing(2),
      color: clientColor || theme.palette.primary.main,
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
    },
    sectionIcon: {
      fontSize: "1rem",
      opacity: 0.7,
      color: clientColor || theme.palette.primary.main,
    },
    contentRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: theme.spacing(2),
      flexWrap: "wrap",
    },
    selectorControl: {
      minWidth: 170,
      "& > div": {
        "&:before": {
          borderBottomWidth: "1px !important",
          borderBottomColor: `${grayColor[4]}!important`,
        },
        "&:after": {
          borderBottomColor: `${clientColor}!important`,
        },
      },
      "& .MuiInputLabel-root": {
        "&.Mui-focused": {
          color: `${clientColor}!important`,
        },
      },
    },
    iconButton: {
      padding: theme.spacing(0.75),
      color: theme.palette.text.secondary,
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
      },
    },
    infoButtonInSection: {
      position: "absolute",
      top: theme.spacing(1),
      right: theme.spacing(1),
      zIndex: 1000,
    },
  }));

export default function SectionModelPnl({
  value,
  onChange,
  clientColor,
  headerTitle,
  variants,
  selectedVariant,
  onVariantSelect,
  pnlPremises,
  infoHtml,
}) {
  const classes = useStyles(clientColor)();
  const T_PREFIX = "rgm.cockpit.scenarioCreation";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  const [infoPopoverAnchor, setInfoPopoverAnchor] = useState(null);

  const handleInfoClick = (event) => {
    setInfoPopoverAnchor(event.currentTarget);
  };

  const handleInfoClose = () => {
    setInfoPopoverAnchor(null);
  };

  return (
    <Box className={classes.sectionContainer}>
      {/* Info Button - Top Right */}
      {infoHtml && (
        <Tooltip title={t("pnl_model_info_tooltip")}>
          <IconButton
            onClick={handleInfoClick}
            className={`${classes.iconButton} ${classes.infoButtonInSection}`}
            size="small"
          >
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Typography className={classes.sectionTitle}>
        <AccountTreeIcon className={classes.sectionIcon} />
        {headerTitle || "Model P&L"}
      </Typography>
      <Box className={classes.contentRow}>
        {/* Dropdown de Variantes */}
        {variants && variants.length > 0 && (
          <FormControl size="small" className={classes.selectorControl}>
            <InputLabel id="model-pnl-variant-label">
              {t("model_variant")}
            </InputLabel>
            <Select
              labelId="model-pnl-variant-label"
              id="model-pnl-variant-select"
              value={selectedVariant || ""}
              onChange={(e) => onVariantSelect(e.target.value)}
              label={t("model_variant")}
              classes={{ select: classes.select }}
              MenuProps={{
                className: classes.selectMenu,
                PaperProps: {
                  className: classes.selectPaper,
                  style: { maxHeight: 300 },
                },
              }}
            >
              {variants.map((variant) => (
                <MenuItem
                  key={variant.variant_id}
                  value={variant.variant_id}
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  {variant.variant_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Dropdown de PnL Premises */}
        <FormControl size="small" className={classes.selectorControl}>
          <InputLabel id="pnl-option-label">{t("pnl_premises")}</InputLabel>
          <Select
            labelId="pnl-option-label"
            id="pnl-option-select"
            value={value}
            onChange={onChange}
            label={t("pnl_premises")}
            classes={{ select: classes.select }}
            MenuProps={{
              className: classes.selectMenu,
              PaperProps: {
                className: classes.selectPaper,
                style: { maxHeight: 300 },
              },
            }}
          >
            {pnlPremises &&
              Object.entries(pnlPremises).map(([key, label]) => (
                <MenuItem
                  key={key}
                  value={key}
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  {label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>

      {/* Info Popover */}
      <PopoverInfoSection
        open={Boolean(infoPopoverAnchor)}
        anchorEl={infoPopoverAnchor}
        onClose={handleInfoClose}
        htmlContent={infoHtml}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </Box>
  );
}

SectionModelPnl.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  clientColor: PropTypes.string,
  headerTitle: PropTypes.string,
  variants: PropTypes.array,
  selectedVariant: PropTypes.number,
  onVariantSelect: PropTypes.func,
  pnlPremises: PropTypes.object,
  infoHtml: PropTypes.string,
};
